import { useEffect } from 'react';
import { ERROR_CODES, ORDER_PAYMENT_STATUSES, OrderType, PaymentGateway } from '../../services/exports/Constants';
import PaymentManager from '../../services/api/PaymentManager';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import { useHandleCheckoutResult } from './useCheckout';
import paymentLog from '../../services/helpers/PaymentLogger';

export const AdyenRedirectResultHandler = () => {
  useHandleAdyenRedirectResult();

  return null;
};

export default function useHandleAdyenRedirectResult() {
  const { company } = useSelector((store: StoreInterface) => store.initialData);

  const urlParams = new URLSearchParams(window.location.search);
  const paymentGateway = urlParams.get('payment_gateway');
  const redirectResult = urlParams.get('redirectResult');
  const checkoutSessionId = urlParams.get('checkout_session_id') ?? undefined;
  const orderUuidFromUrl = urlParams.get('uuid') ?? undefined;

  const { onPaymentSuccess, onPaymentError } = useHandleCheckoutResult(urlParams.get('order_type') as OrderType, {
    setLoading: () => null,
  });

  async function handleRedirectResult() {
    if (paymentGateway === PaymentGateway.Adyen && redirectResult) {
      if (checkoutSessionId) {
        paymentLog.setContext({
          checkout_session_id: checkoutSessionId,
          gateway: 'adyen',
          company_id: company.id,
          ...(orderUuidFromUrl ? { order_uuid: orderUuidFromUrl } : {}),
        });
      }

      paymentLog.info('adyen', 'redirect_result_received', {
        company_id: company.id,
      });

      const { success, response, status } = await PaymentManager.submitAdyenPaymentDetails(
        {
          company_id: company.id,
          adyen_payment_data: {
            redirectResult,
          },
          ...(checkoutSessionId ? { checkout_session_id: checkoutSessionId } : {}),
        },
        {
          // Keep the global error modal's "Continue" as a plain dismiss —
          // without a handler InfoModal falls back to navigating home.
          [ERROR_CODES.CARD_EXCEPTION]: () => null,
        },
      );

      if (success && response.data.data.payment_status === ORDER_PAYMENT_STATUSES.PAID) {
        paymentLog.info('adyen', 'redirect_result_paid', {
          company_id: company.id,
          order_uuid: response.data.data?.uuid,
        });

        paymentLog.clearContext();

        return onPaymentSuccess(response.data.data);
      }

      const errorCode = (response as any)?.response?.data?.error_code;
      const logPayload = {
        company_id: company.id,
        success,
        status,
        error_code: errorCode,
        response,
      };

      if (errorCode === ERROR_CODES.CARD_EXCEPTION) {
        paymentLog.info('adyen', 'redirect_result_failed', logPayload);
      } else {
        paymentLog.error('adyen', 'redirect_result_failed', logPayload);
      }

      return onPaymentError();
    }
  }

  useEffect(() => {
    company?.id && handleRedirectResult();
  }, [company?.id]);
}
